import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RequireAgent } from '@/components/support/RequireAgent';
import { useSupportAuth } from '@/context/SupportAuthContext';
import {
  acceptSupportRequest,
  getRequestWithSecret,
  markNeedMoreInfo,
  rejectSupportRequest,
  transferSupportRequest,
  listActiveAgents,
} from '@/services/supportService';
import { parseUserAgent } from '@/services/supportUtils';
import type { SupportAgent, SupportRequest } from '@/types/support';

function LandingInner() {
  const { requestId = '' } = useParams();
  const [params] = useSearchParams();
  const agentParam = params.get('agent') || '';
  const token = params.get('token') || '';
  const action = params.get('action');
  const { user, agent } = useSupportAuth();
  const navigate = useNavigate();

  const [request, setRequest] = useState<SupportRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [agents, setAgents] = useState<SupportAgent[]>([]);
  const [transferTo, setTransferTo] = useState('');
  const [infoNote, setInfoNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await getRequestWithSecret(requestId);
        if (cancelled) return;
        if (!data) {
          setError('Support request not found.');
          return;
        }
        setRequest(data.request);
        const list = await listActiveAgents();
        setAgents(list.filter((a) => a.agentId !== user?.uid));
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load request');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId, user?.uid]);

  const browserLabel = useMemo(() => {
    if (!request) return '';
    const { browser, device } = parseUserAgent(request.browserInfo.userAgent);
    return `${device} · ${browser}`;
  }, [request]);

  const tokenOk = Boolean(token);
  const agentMatch = !agentParam || agentParam === user?.uid;

  const runAccept = async () => {
    if (!user || !agent || !token) return;
    setBusy(true);
    setError(null);
    const result = await acceptSupportRequest({
      requestId,
      agentId: user.uid,
      accessToken: token,
      agentName: agent.name,
    });
    setBusy(false);
    if (!result.ok) {
      if (result.reason === 'already_assigned') {
        setError('This chat has already been assigned to another support member.');
      } else if (result.reason === 'invalid_token') {
        setError('Invalid or expired accept link.');
      } else {
        setError('This request is no longer waiting.');
      }
      const refreshed = await getRequestWithSecret(requestId);
      if (refreshed) setRequest(refreshed.request);
      return;
    }
    navigate(`/support/dashboard?chat=${requestId}`);
  };

  useEffect(() => {
    if (action === 'accept' && request?.status === 'waiting' && tokenOk && agentMatch && !busy && !loading) {
      void runAccept();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, request?.status, tokenOk, agentMatch, loading]);

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading request…</div>;
  }

  if (!request) {
    return <div className="p-8 text-sm text-destructive">{error || 'Not found'}</div>;
  }

  if (request.status === 'accepted' && request.assignedAgent !== user?.uid) {
    return (
      <div className="max-w-lg mx-auto p-8 space-y-4">
        <h1 className="text-xl font-semibold">Already accepted</h1>
        <p className="text-sm text-muted-foreground">
          This request has already been accepted by another support member.
        </p>
        <Button asChild variant="outline">
          <Link to="/support/dashboard">Open dashboard</Link>
        </Button>
      </div>
    );
  }

  if (request.status === 'accepted' && request.assignedAgent === user?.uid) {
    return (
      <div className="max-w-lg mx-auto p-8 space-y-4">
        <h1 className="text-xl font-semibold">You own this chat</h1>
        <Button asChild>
          <Link to={`/support/dashboard?chat=${requestId}`}>Open conversation</Link>
        </Button>
      </div>
    );
  }

  if (request.status !== 'waiting' && request.status !== 'info_needed') {
    return (
      <div className="max-w-lg mx-auto p-8 space-y-4">
        <h1 className="text-xl font-semibold">Request {request.status}</h1>
        <p className="text-sm text-muted-foreground">This request is no longer available to accept.</p>
        <Button asChild variant="outline">
          <Link to="/support/dashboard">Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (!tokenOk) {
    return (
      <div className="max-w-lg mx-auto p-8">
        <p className="text-sm text-destructive">Missing secure token. Use the link from your email.</p>
      </div>
    );
  }

  if (!agentMatch) {
    return (
      <div className="max-w-lg mx-auto p-8">
        <p className="text-sm text-destructive">
          This accept link was issued for a different agent. Sign in with the correct account.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 md:p-10 space-y-6">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Support request</p>
        <h1 className="text-2xl font-semibold mt-1">Customer waiting</h1>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-sm p-3">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Info label="Customer" value={request.customerName || 'Anonymous visitor'} />
        <Info label="Created" value={new Date(request.createdAt).toLocaleString()} />
        <Info label="Priority" value={request.priority} />
        <Info label="Device" value={browserLabel} />
        <Info label="Page" value={request.pageUrl} className="sm:col-span-2" truncate />
        <Info label="Issue summary" value={request.issueSummary} className="sm:col-span-2" />
      </div>

      <div className="rounded-xl border border-border p-4">
        <h2 className="text-sm font-medium mb-2">Conversation preview</h2>
        <div className="max-h-48 overflow-y-auto space-y-2 text-xs">
          {request.aiHistory.length === 0 && (
            <p className="text-muted-foreground">No AI history attached.</p>
          )}
          {request.aiHistory.slice(-8).map((m, i) => (
            <div key={i} className="rounded-md bg-muted/50 px-2.5 py-1.5">
              <span className="font-medium capitalize">{m.role}: </span>
              {m.content}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void runAccept()} disabled={busy}>
          {busy ? 'Accepting…' : 'Accept'}
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            void (async () => {
              setBusy(true);
              const r = await rejectSupportRequest({ requestId, agentId: user!.uid, accessToken: token });
              setBusy(false);
              if (!r.ok) setError('Could not reject - already handled.');
              else navigate('/support/dashboard');
            })();
          }}
        >
          Reject
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            void (async () => {
              setBusy(true);
              await markNeedMoreInfo({
                requestId,
                agentId: user!.uid,
                accessToken: token,
                note: infoNote || 'Need more information from customer.',
              });
              setBusy(false);
              navigate('/support/dashboard');
            })();
          }}
        >
          Need More Info
        </Button>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-3">
        <h2 className="text-sm font-medium">Transfer (after accept)</h2>
        <p className="text-xs text-muted-foreground">
          Accept first, then transfer from the dashboard. Or pick a teammate to notify after you accept.
        </p>
        <div className="flex flex-wrap gap-2">
          <select
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            value={transferTo}
            onChange={(e) => setTransferTo(e.target.value)}
          >
            <option value="">Select agent…</option>
            {agents.map((a) => (
              <option key={a.agentId} value={a.agentId}>
                {a.name} ({a.email})
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            disabled={!transferTo || busy}
            onClick={() => {
              void (async () => {
                setBusy(true);
                const accept = await acceptSupportRequest({
                  requestId,
                  agentId: user!.uid,
                  accessToken: token,
                  agentName: agent!.name,
                });
                if (!accept.ok) {
                  setError(
                    accept.reason === 'already_assigned'
                      ? 'This chat has already been assigned.'
                      : 'Could not accept before transfer.',
                  );
                  setBusy(false);
                  return;
                }
                await transferSupportRequest({
                  requestId,
                  fromAgentId: user!.uid,
                  toAgentId: transferTo,
                  note: infoNote,
                });
                setBusy(false);
                navigate('/support/dashboard');
              })();
            }}
          >
            Accept & Transfer
          </Button>
        </div>
        <textarea
          className="w-full min-h-[72px] rounded-md border border-border bg-background p-2 text-sm"
          placeholder="Optional note for Need More Info / Transfer"
          value={infoNote}
          onChange={(e) => setInfoNote(e.target.value)}
        />
      </div>
    </div>
  );
}

function Info({
  label,
  value,
  className,
  truncate,
}: {
  label: string;
  value: string;
  className?: string;
  truncate?: boolean;
}) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm mt-0.5 ${truncate ? 'truncate' : 'break-words'}`}>{value}</div>
    </div>
  );
}

export function SupportLandingPage() {
  return (
    <RequireAgent>
      <LandingInner />
    </RequireAgent>
  );
}
